'use client'

import React from 'react'
import {
    composeRenderProps,
    Input as AriaInput,
    InputProps as AriaInputProps,
    TextArea as AriaTextArea,
    TextAreaProps as AriaTextAreaProps,
    TextField as AriaTextField,
    TextFieldProps as AriaTextFieldProps,
} from 'react-aria-components'

import { cn } from '@workspace/ui/lib/utils'
import { FieldGroup } from '@workspace/ui/components/Field'
import { Button } from './Button'
import { EyeClosed, EyeIcon } from 'lucide-react'

const Input = ({ className, ...props }: AriaInputProps) => {
    return (
        <AriaInput
            autoComplete="off"
            className={composeRenderProps(className, className =>
                cn(
                    'shadow-sm flex h-9 w-full rounded-sm bg-background px-3 py-1.5 ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground md:text-sm',
                    'ring-inset ring ring-input',
                    /* Focus Within */
                    'transition-all data-[focused]:ring-primary data-[focused]:ring-2 aria-invalid:ring-destructive',
                    /* Disabled */
                    'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-80',
                    /* Resets */
                    'focus-visible:outline-none',
                    className,
                ),
            )}
            {...props}
        />
    )
}

interface PasswordInputProps extends AriaTextFieldProps {
    placeholder?: string
}

const PasswordInput = ({ className, ...props }: PasswordInputProps) => {
    const [isVisible, setIsVisible] = React.useState(false)

    return (
        <AriaTextField {...props} isInvalid={(props as any)['aria-invalid']}>
            <FieldGroup className={cn('pl-3 pr-1 py-0', className)}>
                <AriaInput
                    type={isVisible ? 'text' : 'password'}
                    className="h-full focus-visible:outline-none flex-1 placeholder:text-muted-foreground"
                />
                <Button variant="ghost" size="iconSm" onClick={() => setIsVisible(!isVisible)}>
                    {isVisible ? <EyeIcon /> : <EyeClosed />}
                </Button>
            </FieldGroup>
        </AriaTextField>
    )
}

const TextArea = ({ className, ...props }: AriaTextAreaProps) => {
    return (
        <AriaTextArea
            className={composeRenderProps(className, className =>
                cn(
                    'shadow-sm flex min-h-[80px] w-full rounded-lg bg-background px-3 py-2 ring-offset-background placeholder:text-muted-foreground md:text-sm',
                    'ring-inset ring ring-input',
                    /* Focus Within */
                    'transition-all data-[focused]:ring-primary data-[focused]:ring-2 aria-invalid:ring-destructive',
                    /* Disabled */
                    'data-disabled:cursor-not-allowed data-disabled:opacity-80 data-disabled:bg-muted',
                    /* Resets */
                    'focus-visible:outline-none',
                    /* Modified the scrollbar */
                    '[&::-webkit-scrollbar]:w-4 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-750 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:cursor-default [&::-webkit-scrollbar-thumb]:border-5 [&::-webkit-scrollbar-thumb]:border-transparent [&::-webkit-scrollbar-thumb]:bg-clip-content',
                    className,
                ),
            )}
            {...props}
        />
    )
}

const PhoneInput = ({
    className,
    value,
    onChange,
    placeholder,
    prefix = '+84',
    maxDigits = 9,
    ...props
}: AriaInputProps & { prefix?: string; maxDigits?: number }) => {
    // Ensure we display digits only (strip optional +84 from value for input)
    const displayValue = (value ?? '').toString().replace(/^\+84/, '')

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const digits = e.target.value.replace(/\D/g, '').slice(0, maxDigits)
        const full = digits ? `${prefix}${digits}` : ''

        if (typeof onChange === 'function') {
            try {
                // Prefer calling as a value for react-hook-form Controller
                ;(onChange as unknown as (val: string) => void)(full)
            } catch (_) {
                // Fall back to event-based onChange signature
                const fakeEvent = { target: { value: full } } as unknown as React.ChangeEvent<HTMLInputElement>
                ;(onChange as unknown as (e: React.ChangeEvent<HTMLInputElement>) => void)(fakeEvent)
            }
        }
    }

    return (
        <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
                {prefix}
            </span>
            <Input
                aria-label="phone"
                className={composeRenderProps(className, className => cn(className, 'pl-12'))}
                value={displayValue}
                onChange={handleChange}
                placeholder={placeholder}
                {...props}
            />
        </div>
    )
}

export { Input, TextArea, PasswordInput, PhoneInput }
